/**
 * The statutory position, stated plainly.
 *
 * This is the product. The Act already gives the applicant a period, an end
 * date and a deemed clearance under s.10; what it does not give them is sight
 * of any of it. Everything in this panel is a date or a count, each one carries
 * where it came from, and the deemed date is never hidden.
 */

import { bi } from "@ksidc/ontology";
import { useLang } from "@ksidc/client";
import type { FileView } from "../api-types.js";
import { COUNTING_RULE_NAMES, days, formatDate, workingDays } from "../format.js";
import { Callout, Fact, ProvenanceRow } from "./ui.js";

export function StatutoryPanel({ file }: { file: FileView }): JSX.Element {
  const { lang, t } = useLang();
  const { clock, breach } = file;
  const workingOnly = clock.countingRule === "WORKING_DAYS_FROM_RECEIPT";

  const headline = !clock.started
    ? bi("The Board has not yet recorded your application as received", "ബോർഡ് നിങ്ങളുടെ അപേക്ഷ ലഭിച്ചതായി രേഖപ്പെടുത്തിയിട്ടില്ല")
    : clock.stopped
      ? bi("The Board has decided. The days are no longer being counted.", "ബോർഡ് തീരുമാനമെടുത്തു. കാലാവധി ഇനി കണക്കാക്കുന്നില്ല.")
      : breach.breached
        ? bi("The Board has passed the last day it had to decide", "നിയമപരമായ കാലാവധി കവിഞ്ഞു")
        : clock.paused
          ? bi("The counting has stopped. The Board is waiting for your answer", "കാലാവധി നിർത്തിവച്ചിരിക്കുന്നു; ഫയൽ നിങ്ങളുടെ പക്കലാണ്")
          : bi("The days are being counted", "കാലാവധി കണക്കാക്കുന്നു");

  const startLabel =
    clock.startBasis === "SUBMISSION"
      ? bi("The day you submitted it", "നിങ്ങൾ അപേക്ഷ സമർപ്പിച്ച ദിവസം")
      : bi("The day the Board received it", "ബോർഡിന് അപേക്ഷ ലഭിച്ച ദിവസം");

  return (
    <section className="notice" aria-labelledby="statutory-heading">
      <p className="notice-head" id="statutory-heading">
        {t(bi("Your time limit", "നിയമപരമായ സ്ഥിതി"))}
      </p>

      <h2 style={{ marginTop: 0 }}>{t(headline)}</h2>

      <p>{t(COUNTING_RULE_NAMES[clock.countingRule] ?? clock.explanation)}</p>
      <ProvenanceRow items={[clock.provenance, clock.conventionProvenance]} />

      <hr className="rule" />

      <div className="facts">
        <Fact label={startLabel} value={formatDate(clock.startDate, lang)} />
        <Fact
          label={bi("The last day the Board has to decide", "കാലാവധിയുടെ അവസാന ദിവസം")}
          value={formatDate(clock.periodEndsOn, lang)}
        />
        {clock.stopped ? (
          <Fact
            label={bi("The day the Board decided", "ബോർഡ് തീരുമാനിച്ച ദിവസം")}
            value={formatDate(clock.stoppedOn, lang)}
          />
        ) : breach.breached ? (
          <Fact
            label={bi("Days past the last day", "അവസാന ദിവസം കഴിഞ്ഞ ദിവസങ്ങൾ")}
            value={t(days(breach.daysOverrun))}
          />
        ) : (
          <Fact
            label={bi("Days still left", "കാലാവധിയിൽ ബാക്കി")}
            value={t(workingDays(clock.daysRemaining, workingOnly))}
          />
        )}
        {clock.pausedDays > 0 ? (
          <Fact
            label={bi(
              "These days were not counted, because the Board was waiting for your answer",
              "കണക്കാക്കാത്ത ദിവസങ്ങൾ, ഒരു ചോദ്യം തീർപ്പാകാതെ ഫയൽ നിങ്ങളുടെ പക്കലായിരുന്നതിനാൽ",
            )}
            value={t(days(clock.pausedDays))}
            provenance={clock.pauseProvenance}
          />
        ) : null}
        <Fact
          label={bi(
            "The day a deemed clearance would arise",
            "അനുമാനിത അനുമതി ലഭിക്കുന്ന ദിവസം",
          )}
          value={formatDate(clock.deemedDate, lang)}
          emphasis
          provenance={breach.provenance}
        />
      </div>

      <hr className="rule" />

      <p>
        <strong>
          {t(
            bi(
              "What the deemed date means.",
              "അനുമാനിത തീയതി എന്താണ് അർത്ഥമാക്കുന്നത്.",
            ),
          )}
        </strong>{" "}
        {t(
          bi(
            `If the Board has neither issued your clearance nor rejected your application by the last day of the period, section 10 treats the clearance as issued from ${formatDate(clock.deemedDate, "en")}, and rule 9(2) prescribes Form C as the certificate for it.`,
            `കാലാവധിയുടെ അവസാന ദിവസത്തിനകം ബോർഡ് അനുമതി നൽകുകയോ അപേക്ഷ നിരസിക്കുകയോ ചെയ്തിട്ടില്ലെങ്കിൽ, വകുപ്പ് 10 പ്രകാരം ${formatDate(clock.deemedDate, "ml")} മുതൽ അനുമതി നൽകിയതായി കണക്കാക്കും; അതിനുള്ള സർട്ടിഫിക്കറ്റ് ചട്ടം 9(2) പ്രകാരം ഫോറം സി ആണ്.`,
          ),
        )}
      </p>

      <Callout
        tone="warn"
        title={bi(
          "The Board has not acted on the deemed date",
          "അനുമാനിത തീയതിയിൽ ബോർഡ് നടപടി എടുത്തിട്ടില്ല",
        )}
        provenance={breach.deemingProvenance}
      >
        <p style={{ marginBottom: 0 }}>
          {t(
            bi(
              "Nothing is given to you automatically on that day. This website records the date and shows it to you and to the board office, because the Act gives you this right and you should know about it. KSIDC has said that a delay should be reported to the grievance committee first. If you want to act on the deemed clearance, decide that with your own legal adviser.",
              "ആ ദിവസം സ്വയമേവ ഒന്നും നൽകുന്നില്ല. ഈ സംവിധാനം ആ തീയതി രേഖപ്പെടുത്തി നിങ്ങളെയും ബോർഡ് ഓഫീസിനെയും അറിയിക്കുന്നു; കാരണം നിയമപ്രകാരം അത് നിങ്ങളുടെ അവകാശമാണ്, അത് അറിയാൻ നിങ്ങൾക്ക് അർഹതയുണ്ട്. കാലതാമസം ആദ്യം മേലധികാരികളുടെ ശ്രദ്ധയിൽ കൊണ്ടുവരണം എന്നാണ് കെ.എസ്.ഐ.ഡി.സി നിർദ്ദേശം. അനുമാനിത അനുമതിയെ ആശ്രയിക്കണോ എന്നത് നിങ്ങളുടെയും നിങ്ങളുടെ ഉപദേശകരുടെയും തീരുമാനമാണ്.",
            ),
          )}
        </p>
      </Callout>

      {clock.paused ? (
        <div style={{ marginTop: "1rem" }}>
          <Callout
            tone="warn"
            title={bi("The days are not being counted right now", "സമയഗണന നിർത്തിവച്ചിരിക്കുന്നു")}
            provenance={clock.pauseProvenance}
          >
            <p style={{ marginBottom: 0 }}>
              {t(
                bi(
                  "The Board has asked you a question. The days stop being counted until you answer. They start again on the day you answer. Go to Messages to read the question and reply.",
                  "ബോർഡ് നിങ്ങളോട് ഒരു ചോദ്യം ചോദിച്ചിട്ടുണ്ട്; അതിന് മറുപടി ലഭിക്കുന്നതുവരെ ദിവസങ്ങൾ കണക്കാക്കുന്നില്ല. നിങ്ങൾ മറുപടി നൽകുന്ന ദിവസം കണക്കാക്കൽ പുനരാരംഭിക്കും. സന്ദേശങ്ങൾ എന്ന താളിൽ മറുപടി നൽകുക.",
                ),
              )}
            </p>
          </Callout>
        </div>
      ) : null}

      {breach.breached ? (
        <div style={{ marginTop: "1rem" }}>
          <Callout
            tone="alarm"
            title={bi(
              "The Board is past its time limit",
              "ബോർഡ് സമയപരിധി കഴിഞ്ഞു",
            )}
            provenance={file.escalation.provenance}
          >
            <p>
              {t(
                bi(
                  `The period ended on ${formatDate(clock.periodEndsOn, "en")}, which was ${days(breach.daysOverrun).en} ago.`,
                  `കാലാവധി ${formatDate(clock.periodEndsOn, "ml")}-ന് അവസാനിച്ചു, അതായത് ${days(breach.daysOverrun).ml} മുൻപ്.`,
                ),
              )}
            </p>
            <p style={{ marginBottom: 0 }}>
              {t(
                bi(
                  `If the Board is late, you can complain to the ${file.escalation.forum.en}. Which committee it is depends on how much your project costs.`,
                  `ബോർഡ് വൈകിയാൽ, നിങ്ങൾക്ക് ${file.escalation.forum.ml}-യിൽ പരാതി നൽകാം. ഏത് സമിതി എന്നത് നിങ്ങളുടെ പദ്ധതിച്ചെലവ് അനുസരിച്ചാണ്.`,
                ),
              )}
            </p>
          </Callout>
        </div>
      ) : null}

      <p className="muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
        {t(clock.explanation)}
      </p>
    </section>
  );
}
